import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Dimension } from '../lib/types';
import { clamp } from '../lib/format';
import { InfoPopover } from './InfoPopover';
import styles from './DimensionGauge.module.css';

interface Props {
  dim: Dimension;
  /** Optional explainer for what this dimension measures. */
  tooltip?: ReactNode;
  className?: string;
}

const tierToTone: Record<string, string> = {
  HIGH: 'var(--tia-positive)',
  ELEVATED: 'var(--tia-positive)',
  MODERATE: 'var(--tia-warning)',
  LOW: 'var(--tia-negative)',
};

export function DimensionGauge({ dim, tooltip, className }: Props) {
  const [open, setOpen] = useState(false);
  const pct = clamp(dim.value, 0, 1) * 100;
  const tone = tierToTone[dim.qualifier] ?? 'var(--fg-muted)';
  const hasEvidence = (dim.evidence?.length ?? 0) > 0;

  return (
    <div className={[styles.row, className].filter(Boolean).join(' ')}>
      <div className={styles.head}>
        <span className={styles.labelWrap}>
          <span className={styles.label}>{dim.label}</span>
          {tooltip && <InfoPopover label={`What is ${dim.label}?`} content={tooltip} />}
        </span>
        <span className={styles.qualifier} style={{ color: tone, borderColor: tone }}>
          {dim.qualifier}
        </span>
      </div>
      <div className={styles.barWrap} role="meter" aria-valuemin={0} aria-valuemax={1} aria-valuenow={dim.value} aria-label={dim.label}>
        <div className={styles.barTrack}>
          <div className={styles.barFill} style={{ width: `${pct}%` }} />
        </div>
        <span className={[styles.barValue, 'mono'].join(' ')}>{(dim.value * 100).toFixed(0)}</span>
      </div>
      {hasEvidence && (
        <button
          type="button"
          className={styles.evidenceToggle}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          Evidence
          <ChevronDown
            size={14}
            strokeWidth={1.5}
            className={[styles.chev, open ? styles.chevOpen : ''].join(' ')}
            aria-hidden="true"
          />
        </button>
      )}
      {open && hasEvidence && (
        <ul className={styles.evidenceList}>
          {dim.evidence!.map((e, i) => (
            <li key={i} className={styles.evidenceItem}>
              <span className={styles.quote}>“{e.quote}”</span>
              <span className={styles.ref}>{e.ref}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
